import { AuthContext } from '@repo-level/core-front';
import {
  ScNavBadge,
  ScNewNavBadge,
} from 'components/general/Menu/MenuDesktop/styled';
import { MissionsContext } from 'contexts/missions';
import { RealTimeServiceContext } from 'contexts/realTimeService';
import {
  groupMember,
  groupMemberDocumentsRequested,
  groupMemberVerified,
  USER_GROUP,
} from 'helpers/user';
import { useTheme } from 'hooks/theme';
import { useAuroraCrystalAvailable } from 'modules/crystal/hooks';
import { sendSearchOverallAppearanceEvent } from 'modules/googleAnalytics/events/searchGamesEvents';
import { getSpinsLeftAmount } from 'modules/wheel/components/helpers';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLuckyWheel } from './luckyWheel';

interface MenuItem {
  name: string;
  url: string;
  icon?: JSX.Element;
  iconSettings?: {
    isStroke?: boolean;
  };
  badge?: JSX.Element | null;
  isHidden?: boolean;
  sendEvent?: () => void;
}

export const useHeaderMenuList = () => {
  const { t } = useTranslation();
  const { images } = useTheme();
  const { isAuthenticated, pendingUser, user } = useContext(AuthContext);
  const { lootboxes } = useContext(RealTimeServiceContext);

  const { isExtraLootbox } = useLuckyWheel();

  const isVerifiedUser = useMemo(() => groupMemberVerified(user), [user]);

  const hasDocumentsRequested = useMemo(
    () => groupMemberDocumentsRequested(user),
    [user]
  );

  const spinsLeftAmount = useMemo(
    () => (isAuthenticated ? getSpinsLeftAmount(lootboxes || [], user) : null),
    [isAuthenticated, lootboxes, user]
  );

  const { isAuroraCrystalPromoDate } = useAuroraCrystalAvailable();

  const { data: missionsCMSData, isShowQuests } = useContext(MissionsContext);

  const headerMenuList = useMemo(
    (): MenuItem[] => [
      {
        name: t('glossary:home'),
        url: '/',
        icon: <images.homeIcon.component />,
      },
      {
        name: t('glossary:fortune'),
        url: '/bonuses',
        icon: <images.fortuneIcon.component />,
      },
      ...(isAuroraCrystalPromoDate
        ? []
        : [
            {
              name: t('glossary:luckyWheel'),
              url: '/lucky-wheel',
              icon: <images.wheelIcon.component />,
              badge:
                !!spinsLeftAmount || isExtraLootbox ? (
                  <ScNavBadge withOpacity={!isExtraLootbox}>
                    <div className="bg" />
                    <div className="content">
                      {isExtraLootbox ? (
                        <images.star.component />
                      ) : (
                        spinsLeftAmount
                      )}
                    </div>
                  </ScNavBadge>
                ) : null,
              isHidden:
                pendingUser || (user && groupMember(USER_GROUP.BR2)(user)),
            },
          ]),
      {
        name: t('glossary:jackPot'),
        url: '/jackpot',
        icon: <images.jackpotIcon.component />,
      },
      {
        name: t('glossary:tournaments'),
        url: '/tournaments',
        icon: <images.tournamentIcon.component />,
      },
      {
        name: t('glossary:loyalty'),
        url: '/loyalty',
        icon: <images.loyaltyIcon.component />,
      },
      ...(isShowQuests &&
      user &&
      !pendingUser &&
      !!missionsCMSData?.missionsBlocks?.length
        ? [
            {
              name: t('glossary:quests'),
              url: '/quests',
              icon: <images.questsIcon.component />,
              badge: <ScNewNavBadge>{t('glossary:new')}</ScNewNavBadge>,
            },
          ]
        : []),
      {
        name: t('glossary:vip'),
        url: '/vip',
        icon: <images.vipIcon.component />,
        iconSettings: { isStroke: true },
      },
      {
        name: t('glossary:searchGames'),
        url: '/search-games',
        icon: <images.searchIcon.component />,
        sendEvent: () => sendSearchOverallAppearanceEvent('menu'),
      },
      {
        name: t('glossary:profile'),
        url: '/profile-custom/personal',
        icon: <images.profileIcon.component />,
        badge:
          isVerifiedUser || hasDocumentsRequested ? (
            <ScNavBadge>
              {isVerifiedUser && !hasDocumentsRequested && (
                <images.verified.component />
              )}
              {hasDocumentsRequested && <images.unverified.component />}
            </ScNavBadge>
          ) : null,
        isHidden: !isAuthenticated,
      },
      {
        name: t('glossary:mobileApp'),
        url: '/mobile-app',
        icon: <images.mobileAppIcon.component />,
      },
    ],
    [
      isAuroraCrystalPromoDate,
      images,
      hasDocumentsRequested,
      isAuthenticated,
      isShowQuests,
      isExtraLootbox,
      isVerifiedUser,
      missionsCMSData,
      pendingUser,
      spinsLeftAmount,
      t,
      user,
    ]
  );
  return headerMenuList;
};
